import React, { useEffect } from 'react'
import { ActivityIndicator, Linking } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { publishEvent } from '@zet/analytics'
import { useToast } from '@zet/common/components/toast'
import { AgentDetails } from '@zet/customer'
import { Button, Stack, colors } from '@zet/design-system'
import { i18n } from '@zet/localization'
import { replaceLoginHash } from '@zet/utils/loginHash'
import {
  MOBILE_NUMBER_REGEX,
  NAME_REGEX,
  PINCODE_REGEX,
} from '@zet/utils/regularExpression'
import {
  GENERIC_ALPHABET_FIELD_VALIDATION,
  MOBILE_NUMBER_VALIDATION,
  NAME_FIELD_VALIDATION,
} from '@zet/utils/validationMessage'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useGetNextStateForProductFlow } from '../../../../api/brands/hooks'
import { BottomsheetTypes } from '../../../../api/brands/types'
import { useProductFlowApply } from '../../../../hooks/useProductFlowApply'
import {
  handlePrefill,
  handleSettingValueToSessionStorage,
} from '../../../../utils'
import { Header } from '../header'
import { InputFields } from './inputFields'

const validationSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, { message: 'Required' })
    .regex(new RegExp(NAME_REGEX), NAME_FIELD_VALIDATION),
  phoneNumber: z
    .string()
    .min(1, { message: 'Required' })
    .regex(new RegExp(MOBILE_NUMBER_REGEX), MOBILE_NUMBER_VALIDATION)
    .max(10, { message: MOBILE_NUMBER_VALIDATION }),
  pinCode: z
    .string()
    .min(1, { message: 'Required' })
    .length(6, { message: 'Not a valid PIN code' })
    .regex(PINCODE_REGEX, 'Invalid Pin Code'),
  advisorCode: z
    .string()
    .regex(
      new RegExp('.*^[A-Za-z0-9]{2,254}.*'),
      GENERIC_ALPHABET_FIELD_VALIDATION,
    )
    .optional(),
  email: z.string().email({ message: 'Invalid email' }),
})

const PersonalDetails = ({
  agentData,
  handleBottomsheetState,
}: {
  agentData: AgentDetails
  handleBottomsheetState: (state: BottomsheetTypes, data?: any) => void
}) => {
  const isMagnetFLow = agentData?.partnerId === 193
  const updatedValidationSchema = isMagnetFLow
    ? validationSchema.omit({ pinCode: true })
    : validationSchema
  type ValidationSchema = z.infer<typeof updatedValidationSchema>
  const toast = useToast()
  const {
    control,
    formState: { errors, isValid },
    getValues,
    setValue,
    handleSubmit,
    trigger,
    setError,
  } = useForm<ValidationSchema>({
    mode: 'onChange',
    resolver: zodResolver(updatedValidationSchema),
  })
  const { mutate, isLoading } = useGetNextStateForProductFlow()
  function closeBottomSheet() {
    handleBottomsheetState(null)
  }

  const { apply, shareLinkLoading } = useProductFlowApply(
    agentData,
    handleBottomsheetState,
  )

  function onSubmit(values: {
    fullName: string
    phoneNumber: number | string
    pinCode: number | string
    advisorCode: number | string
    email: string
  }) {
    publishEvent('click_action', {
      item_name: 'shortflow_personal_details',
    })
    handleSettingValueToSessionStorage(getValues, agentData)
    mutate(
      {
        userState: 'SHOWN_PHONE_NUMBER_SCREEN',
        customerPayload: {
          fullName: values?.fullName,
          phoneNumber: values?.phoneNumber,
          categoryId: agentData?.categoryId,
          pinCode: values?.pinCode,
          customerIntentList: [''],
          email: values.email,
          ...(agentData?.isDsa
            ? {
                meta: {
                  advisorCode: values?.advisorCode,
                },
              }
            : {}),
        },
      },
      {
        onSuccess(data) {
          if (data?.actionData?.hash) {
            replaceLoginHash(data?.actionData?.hash)
          }
          if (data?.actionData?.customerId) {
            sessionStorage.setItem('customerId', data?.actionData?.customerId)
          }
          if (data?.actionData?.lockedTill) {
            toast?.show({
              intent: 'error',
              heading: i18n.t('toastMessages.error'),
              description: data?.actionData?.reason,
              hideAfter: 3000,
            })
            return data?.actionData
          }
          if (data?.code === 400) {
            setError('pinCode', {
              message: data?.message,
            })
          }
          if (data?.action === 'SHOW_OTP_SCREEN') {
            handleBottomsheetState('OTP_VERIFICATION', data)
          } else if (data?.action === 'SHOW_PAN_COLLECTION_SCREEN_V3') {
            handleBottomsheetState('BASIC_DETAILS')
          } else if (data?.action === 'SHOW_BASIC_DETAILS_SCREEN_V3') {
            handleBottomsheetState('EMPLOYMENT_DETAILS')
          } else if (data?.action === 'REDIRECTION_URL') {
            closeBottomSheet()
            if (Platform.OS === 'web') {
              window.location.href = data?.actionData?.url
            } else {
              Linking.openURL(data?.actionData?.url)
            }
          } else if (data?.action === 'SHOW_SECONDARY_OTP_SCREEN_V3') {
            const storedSecondaryPhoneNumber = sessionStorage.getItem(
              'secondaryPhoneNumber',
            )
            handleBottomsheetState('OTP_SECONDARY_PHONE_VERIFICATION', {
              secondaryPhoneNumber:
                data?.actionData?.secondaryPhoneNumber ??
                storedSecondaryPhoneNumber,
            })
          } else if (data?.action === 'SHOW_SECONDARY_PHONE_NUMBER_SCREEN_V3') {
            handleBottomsheetState('SECONDARY_PHONE_NUMBERS', data)
          } else if (data?.action === 'SHOW_RECOMMENDATION_SCREEN') {
            apply()
          }
        },
        onError(err) {
          toast?.show({
            intent: 'error',
            heading: i18n.t('toastMessages.error'),
            description:
              err?.response?.data?.error?.message ||
              i18n.t('toastMessages.somethingWentWrong'),
            hideAfter: 3000,
          })
        },
      },
    )
  }

  useEffect(() => {
    handlePrefill(setValue, trigger, agentData)
  }, [])

  return (
    <>
      <Header
        title={i18n.t('addCusProductjourney.addPersonal')}
        closeBottomSheet={closeBottomSheet}
      />
      {isLoading || shareLinkLoading ? (
        <Stack className="pt-6">
          <ActivityIndicator
            size={'large'}
            color={colors.primary[100]}
            className="items-center justify-center"
          />
        </Stack>
      ) : (
        <InputFields agentData={agentData} control={control} errors={errors} />
      )}
      <Stack className="bg-neutral-white100 absolute bottom-0 w-full px-3 py-2">
        <Button
          className="bg-tertiary-200 w-full"
          disabled={!isValid}
          onPress={handleSubmit(onSubmit)}
          size={'lg'}
          loading={isLoading || shareLinkLoading}
        >
          {i18n.t('bottomView.continue')}
        </Button>
      </Stack>
    </>
  )
}

export { PersonalDetails }
